import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useOnboarding } from "../context/OnboardingContext";
import { safeRedirectTarget } from "../lib/redirectTarget";
import {
  ONBOARDING_PATH,
  ONBOARDING_STEP_COUNT,
  isProfileStepComplete,
  normalizeStep,
  stepNumber,
  withShortGoal,
  PROFILE_LOAD_FAILED_TITLE,
  PROFILE_LOAD_FAILED_BODY,
  PROFILE_LOAD_FAILED_RETRY,
  STEP_SAVE_FAILED,
} from "../lib/onboarding";
import PlayerProfile from "./PlayerProfile";
import { Progress } from "../components/ui/progress";
import { StepCounter, SecondaryAction, ActionButton, QuoteEchoCard } from "../components/kit";
import { CloudOff, RotateCw } from "lucide-react";
import { toast } from "sonner";
import { arrivalToastSuppressed } from "../lib/arrivalToastGate";
import { leaveSetUpTo } from "../lib/tour";
import { track } from "../lib/usageTracker";

/**
 * The set-up walkthrough, redrawn to the approved screen (issue #754, wave 1
 * of epic #749). Rendered instead of `pages/Onboarding.jsx` when
 * `newUi("setup")` is on (see the route ternary in `App.js`) — the shipped
 * screen is untouched with the flag off, and this file is a sibling, not a
 * replacement in place.
 *
 * STEP 1 IS PRESENTATION ONLY. Every field, every validation rule and the
 * step-1-to-2 transition are copied from `Onboarding.jsx` unchanged: same
 * hooks (`PlayerProfile.jsx`, unmodified save calls), same
 * `isProfileStepComplete` (lib/onboarding.js). What changes is composition
 * — the step label moves out of the page body's old gold eyebrow and
 * becomes a kit `StepCounter variant="eyebrow"` (slate, `#64748B` — rule
 * R-003, one gold element per screen, which is Continue/Finish), the
 * footer becomes the kit's `SecondaryAction` / `ActionButton` pair, and the
 * surrounding chrome comes from `Layout.jsx`'s `CHROMELESS_ROUTES` gate
 * rather than this file. Step 2 (below) is NOT presentation-only — see its
 * own note.
 *
 * WHY NOT A SECOND `<TopBar>`. The approved drawing (`SetUp.dc.html`) draws
 * one top bar, and #730/#754 name it explicitly as `Layout.jsx:292-357` —
 * the app's REAL topbar, kept for this route because `AccountMenu` there is
 * the only sign-out below `md`. The kit's `TopBar` component always renders
 * its own logo, a decorative bell/info pair and an empty avatar placeholder
 * (`components/kit/TopBar.jsx`) — none of it wired to `NotificationsBell` /
 * `AccessibilityMenu` / the real `AccountMenu`. Rendering it here as well
 * would stack a second, non-functional bar under the first. So this page
 * composes the kit's `StepCounter` alone for the step label, and leaves the
 * one real bar to `Layout`.
 *
 * STEP 2 IS ITS OWN DRAWN SCREEN NOW (#872, D172 S2). It used to render
 * `PlayerIdentity embedded` — the WHOLE Player Vision `SectionCard`, twenty-
 * five questions' worth of chrome for one field the drawing (`Plan.dc.html`,
 * state `set-up-step-2`) asks alone. D172 S2 rules that step 2 renders
 * `pi-short-goal` and nothing else, and saves only `short_term_goal` —
 * never the six other vision fields a returning player might already have
 * on file. `PlayerIdentity.jsx` itself is UNTOUCHED (the shipped
 * `Onboarding.jsx`'s own `embedded` call, and `/development?tab=identity`'s
 * un-embedded one, both still need its full form); this file no longer
 * imports it at all, and saves through its own `saveVisionStep` below —
 * load-merge-write, the same safe pattern `PlayerIdentity.jsx`'s own
 * `mergeLoadedIdentity` uses, so a field this screen never shows can't be
 * wiped by a PUT that only meant to change one of the eight.
 */
export default function OnboardingNew() {
  const [sp, setSp] = useSearchParams();
  const nav = useNavigate();
  const location = useLocation();
  const { status, step: serverStep, applyStatus } = useOnboarding();

  // Where to go when this is over. Validated, never trusted — history state
  // is attacker-writable (see lib/redirectTarget.js).
  const target = safeRedirectTarget(location.state?.from);

  // The URL owns the step, exactly as it does in Onboarding.jsx.
  const requestedStep = sp.get("step");
  const step = normalizeStep(requestedStep || serverStep || "profile");
  useEffect(() => {
    if (requestedStep !== step) {
      const next = new URLSearchParams(sp);
      next.set("step", step);
      setSp(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, requestedStep]);

  const profileControl = useRef(null);
  const [profileReady, setProfileReady] = useState(false);
  // #1077 (D118, D57) — SetUp.dc.html's `profile-could-not-load`: the
  // GET /players/me failed, so the form would be an empty one that looks
  // like "no profile yet". PlayerProfile reports it; the retry is its
  // `controlRef.current.reload`.
  const [profileLoadFailed, setProfileLoadFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  // #868 (D172 S6) — step 1's save failure, on the page, D57/D55. Cleared
  // optimistically the moment Continue is tapped again, so a second
  // attempt never shows a stale failure alongside its own outcome.
  const [profileSaveFailed, setProfileSaveFailed] = useState(false);
  // #872 (PR #937 review, F2) — step 2's two failure modes, same D57/D55
  // rule as step 1's: never a toast on this leg (this whole file only
  // ever renders on the flag-on leg — Onboarding.jsx is the separate,
  // untouched flag-off file, and it keeps its own toasts). `null` | "load"
  // (the identity GET never resolved, so saving would overwrite the other
  // seven vision answers with nothing — same refusal PlayerIdentity.jsx's
  // own `save` makes) | "save" (the PUT itself failed).
  const [visionSaveError, setVisionSaveError] = useState(null);
  // #872 (PR #937 review, F2) — the shared `/onboarding/complete` failure
  // (`stopAsking`, below), which both Skip (either step) and Finish (step
  // 2, after its own save) route through. One flag, not two, because it
  // is the same call either way and only one of Skip/Finish can be in
  // flight at once (`busy`).
  const [completeFailed, setCompleteFailed] = useState(false);

  // #872 (D172 S2) — step 2's own load-merge-write state. `visionRef` holds
  // the full `vision` object as last known on the server (every field,
  // whether this screen shows it or not); `save` sends THAT object back
  // with only `short_term_goal` changed, so the six fields this screen
  // never renders survive a save here exactly as `PlayerIdentity.jsx`'s own
  // `mergeLoadedIdentity` keeps them safe on the tab that does show them.
  // `visionLoadRef`/`visionLoadFailedRef` mirror `PlayerIdentity.jsx`'s own
  // `loadRef`/`loadFailedRef` — the mount race is the same race (the player
  // can reach step 2 and start typing before the GET resolves), and refusing
  // to save on top of a load that never arrived is the same honest answer
  // (see PlayerIdentity.jsx's `save`). `visionDirtyRef` stops the load from
  // clobbering a sentence already typed before it resolved.
  const visionRef = useRef({});
  const visionLoadRef = useRef(null);
  const visionLoadFailedRef = useRef(false);
  const visionDirtyRef = useRef(false);
  const [shortGoal, setShortGoal] = useState("");
  const shortGoalRef = useRef(null);

  useEffect(() => {
    visionLoadRef.current = api
      .get("/players/me/identity")
      .then((r) => {
        const loaded = (r.data && r.data.vision) || {};
        visionRef.current = { ...loaded };
        if (!visionDirtyRef.current) {
          setShortGoal(typeof loaded.short_term_goal === "string" ? loaded.short_term_goal : "");
        }
      })
      .catch(() => {
        visionLoadFailedRef.current = true;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goTo = (nextStep) =>
    nav(`${ONBOARDING_PATH}?step=${nextStep}`, { replace: true, state: location.state });

  /** Finish or skip: one bodyless POST, whichever it was. Mirrors
   * Onboarding.jsx's `stopAsking` in EFFECT (same endpoint, same
   * bodyless POST) but not in the failure path any more — PR #937
   * review, F2: this file's own failure shows on the page (D57/D55,
   * `completeFailed` below), never a toast; `Onboarding.jsx` is untouched
   * and keeps its toast on the flag-off leg. */
  const stopAsking = async () => {
    try {
      const { data } = await api.post("/players/me/onboarding/complete");
      applyStatus(data);
      track("onboarding.done"); // #1594: finished or skipped; a skip has no identity step_done before it
      return true;
    } catch (e) {
      console.error("OnboardingNew: failed to record completion", e);
      setCompleteFailed(true);
      return false;
    }
  };

  // #1514 (D406): a device that has not seen the player tour goes to it first,
  // with the original target in router state so the tour's Skip and last card
  // continue to it. Flag off or tour seen: straight to the target, as before.
  const leaveTo = (to) => leaveSetUpTo(nav, "player", to);

  const skip = async () => {
    // #938 nit 1: Skip saves nothing, so a stale "save failed" banner from
    // an earlier Finish attempt on step 2 (`visionSaveError`) must not
    // survive it — only `completeFailed` was cleared here, leaving
    // `onboarding-vision-save-error` up under a Skip that did nothing
    // wrong.
    setVisionSaveError(null);
    setCompleteFailed(false);
    setBusy(true);
    const ok = await stopAsking();
    setBusy(false);
    if (ok) leaveTo(target);
  };

  const continueFromProfile = async () => {
    setProfileSaveFailed(false);
    setBusy(true);
    const saved = await profileControl.current?.save();
    setBusy(false);
    if (saved) {
      track("onboarding.step_done", { item: "profile" });
      goTo("identity");
    }
    else setProfileSaveFailed(true);
  };

  // #872 (D172 S2) — sends the FULL vision object back, with only
  // `short_term_goal` touched, the same "load first, patch one field,
  // write the whole section" shape `PlayerIdentity.jsx`'s own `save`
  // uses (that file's own comment on `PlayerIdentityPatch` explains why:
  // `PUT /players/me/identity` replaces the section wholesale server-side,
  // `api/src/db/repos/playerIdentity.ts`'s `upsertPlayerIdentity`, so a
  // payload missing a field the player answered elsewhere would erase it,
  // not skip it). Refuses to save on top of a load that never resolved,
  // same posture as `PlayerIdentity.jsx`'s save — PR #937 review, F2: both
  // failure branches set `visionSaveError` for the page to render (D57/
  // D55) instead of a toast.
  const saveVisionStep = async () => {
    try {
      await visionLoadRef.current;
      if (visionLoadFailedRef.current) {
        setVisionSaveError("load");
        return false;
      }
      const nextVision = withShortGoal(visionRef.current, shortGoal);
      await api.put("/players/me/identity", { vision: nextVision });
      visionRef.current = nextVision;
      return true;
    } catch {
      setVisionSaveError("save");
      return false;
    }
  };

  const finish = async () => {
    setVisionSaveError(null);
    setCompleteFailed(false);
    setBusy(true);
    const saved = await saveVisionStep();
    if (!saved) { setBusy(false); return; }
    track("onboarding.step_done", { item: "identity" });
    const ok = await stopAsking();
    setBusy(false);
    if (ok) {
      // D109 (PR #846, second review) — see arrivalToastGate.js.
      if (!arrivalToastSuppressed(target)) toast.success("You're all set!");
      leaveTo(target);
    }
  };

  const isProfileStep = step === "profile";
  // #872 (D172 S3) — Finish is gated on the ONE field this screen shows,
  // trimmed and non-empty, never `hasVisionContent` (which reads all eight
  // vision fields — G9.4's finding: a returning player with an old
  // `why_play` on file could Finish here with THIS sentence still empty).
  const trimmedShortGoal = shortGoal.trim();
  const canContinue = isProfileStep ? profileReady && !profileLoadFailed : trimmedShortGoal !== "";

  // #868 (D172 S1) — Continue stays full gold and reachable while step 1 is
  // incomplete; a tap neither saves nor advances, it moves focus to the
  // first field the completeness rule (`isProfileStepComplete`,
  // lib/onboarding.js) is still waiting on. `aria-disabled` on the button
  // itself (below) is the accessibility half of the same ruling — the
  // control announces "disabled" without the native attribute stopping
  // this handler from running.
  const handleContinueTap = () => {
    if (busy) return;
    if (!canContinue) {
      // The form is hidden while its load has failed — nothing to focus.
      if (!profileLoadFailed) profileControl.current?.focusFirstMissing?.();
      return;
    }
    continueFromProfile();
  };

  return (
    <div className="nw-page-stack" data-testid="onboarding-page">
      {/* Issue #792 — this stack was `space-y-6` (Tailwind, `rem`), which
          rendered at 25.5px against SetUp.dc.html:64's drawn 14px and moved
          with the text-size control (28.5px at Large). `.nw-page-stack`
          (index.css) is fixed px from `--nw-space-stack`, same rule as the
          type ramp one level up. PR #826 review fix round: the header-block
          spacing (mt-1/mt-2/mt-4, below) and the footer button gap
          (gap-2.5, below) are now fixed too — see each site's own comment.
          The already-done note (#1226) is a kit surface with fixed-px
          padding, so it carries no `rem` spacing utility. */}
      {/* A marker only THIS component renders — epic #749's per-screen-slice
          rule (comment on #749, following #753's `assertSignUpScreenMatchesLeg`):
          a journey helper that tolerates either screen (there is none here
          today, but the pattern is the same) must not be the only thing
          standing between a flag that silently failed to flip and a test
          that stayed green anyway. `onboarding-step-label` and every
          `onboarding-*`/`pp-*` testid below are shared with the shipped
          `Onboarding.jsx`, so none of them can prove which screen actually
          rendered — this one, introduced here and nowhere else, can. */}
      <span data-testid="onboarding-new-screen" className="sr-only" aria-hidden="true" />
      {/* PR #826 review, header-block fold-in — `mt-1`/`mt-2`/`mt-4`
          (Tailwind, `rem`) rendered this block's gaps at 8.75/8.5/17px and
          moving (17px is visible), against SetUp.dc.html:66's drawn
          `gap: 6px` flex column wrapping the eyebrow, title, lede AND the
          progress bar together — the same uniform rhythm CoachSetUp.dc.html:61
          and CoachPortalEmpty.dc.html:57 draw for their own header blocks.
          `.nw-stack-tight` (index.css) is fixed px from the existing
          `--nw-space-tight` token (6px) — the same class the "rest lives in
          Settings"/labelled-empty boxes already use, reused here for the
          same 6px rhythm rather than a fourth token. */}
      <div className="nw-stack-tight">
        {/* The shipped gold eyebrow (Onboarding.jsx:141) competed with the
            gold Continue button on the same screen — R-003 allows exactly
            one gold element, and Continue is it. `variant="eyebrow"` is
            the drawn treatment for the label placed in the page body
            (SetUp.dc.html:76: 10px/700/0.18em uppercase, --nw-ink-muted) —
            PR #777 review F1: `tone="navy"` rendered .nw-caption navy,
            which is neither the shipped gold nor the drawn slate. */}
        <StepCounter
          step={stepNumber(step)}
          total={ONBOARDING_STEP_COUNT}
          variant="eyebrow"
          data-testid="onboarding-step-label"
        />
        {/* #872 (D172 S4) — Plan.dc.html's `set-up-step-2` state draws this
            title at 22px, one size below the kit's own `.nw-title` (24px,
            `index.css`) — a deviation from kit decision 1, not a new class
            (brief-g59.md's own ruling: "Recorded as a deviation ... not a
            new class"). Step 1's title is unaffected; SetUp.dc.html draws
            it at the ordinary 24px. */}
        <h1 className="nw-title" style={isProfileStep ? undefined : { fontSize: 22 }}>
          {isProfileStep ? "Set up your player profile" : "What are you working on this season?"}
        </h1>
        <p className="nw-lede">
          {isProfileStep
            ? "Two quick steps before you start logging. First, how you play — your role, and whether you bat, bowl or both. A photo is not required."
            : "One thing, in your own words. You can change it later on your plan."}
        </p>
        {/* #872 (G9.2) — Plan.dc.html's `set-up-step-2` state draws no
            progress track at all (its header sub-block is label/title/lede
            only); the shared bar stays on step 1.
            Merge note (main #933/D171 X1): main's own fix for this same
            track used an inline `bg-[var(--nw-line))]` utility; `.nw-kit-
            progress` (index.css, #868/#916 G5.6) already covers that exact
            token AND the navy fill shadcn's own default also gets wrong,
            with `!important` to beat the compiled Tailwind utility's own
            cascade position — kept in favour of main's narrower one-
            property fix. */}
        {isProfileStep && (
          <Progress
            value={(stepNumber(step) / ONBOARDING_STEP_COUNT) * 100}
            className="h-2 nw-kit-progress"
            data-testid="onboarding-progress"
          />
        )}
      </div>

      {/* Kept: the drawing omits this, but it is wrong to (journey 11
          exercises the resume state — dropping it breaks a real
          behaviour). Redrawn as a kit surface in #1226; copy unchanged. */}
      {status && !status.required && (
        // #1226 — the kit's surface card (the same recipe as the load-failure
        // block below), not the shipped shadcn `Card`. Copy and testid are
        // unchanged.
        <div
          data-testid="onboarding-already-done"
          className="nw-lede"
          style={{
            padding: "var(--nw-space-card)",
            background: "var(--nw-surface)",
            border: "1px solid var(--nw-line)",
            borderRadius: "var(--nw-radius-card)",
          }}
        >
          You've already been through this — anything you change here saves exactly as it does on your
          profile and development pages.
        </div>
      )}

      {isProfileStep ? (
        // `frameless` (PR #777 review, F6): the drawing has no card around
        // the form — fields sit directly on the page background. This is
        // an existing PlayerProfile prop, built for exactly this ("a
        // frameless card in a bordered card reads as a rendering mistake" —
        // PlayerProfile.jsx's own docstring).
        // `photoLast` (#785, review F3): SetUp.dc.html puts the photo block
        // after Playing role, not first — named explicitly rather than
        // inferred from `embedded && frameless` (see PlayerProfile.jsx's
        // own docstring for the two-host decision this prop encodes).
        // `kitType` (#830): this screen is the ONLY caller that sets it —
        // Settings and the shipped Onboarding.jsx both render this same
        // component with no `kitType` at all, so both stay byte-identical
        // (PlayerProfile.jsx's own docstring explains the mechanism).
        // #1077 — PlayerProfile stays MOUNTED while its load has failed (it
        // owns the GET and the retry), so the empty form is hidden, not
        // unmounted; the drawn failure block takes its place.
        <>
          {profileLoadFailed && (
            <div
              data-testid="onboarding-profile-load-error"
              className="flex flex-col items-center text-center min-w-0"
              style={{
                gap: 6,
                padding: "20px 16px",
                background: "var(--nw-surface)",
                border: "1px solid var(--nw-line)",
                borderRadius: "var(--nw-radius-card)",
              }}
            >
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 14,
                  background: "var(--nw-line-faint)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CloudOff size={20} color="var(--nw-alert)" aria-hidden="true" />
              </div>
              <div className="break-words" style={{ fontWeight: 600, fontSize: 14, color: "var(--nw-ink)" }}>
                {PROFILE_LOAD_FAILED_TITLE}
              </div>
              <div className="break-words" style={{ fontSize: 12, lineHeight: 1.5, color: "var(--nw-ink-muted)" }}>
                {PROFILE_LOAD_FAILED_BODY}
              </div>
              <SecondaryAction
                variant="boxed"
                onClick={() => profileControl.current?.reload?.()}
                data-testid="onboarding-retry"
                style={{ marginTop: 6, display: "inline-flex", alignItems: "center", gap: 6 }}
              >
                <RotateCw size={14} aria-hidden="true" />
                {PROFILE_LOAD_FAILED_RETRY}
              </SecondaryAction>
            </div>
          )}
          <div hidden={profileLoadFailed}>
            <PlayerProfile
              embedded
              frameless
              photoLast
              kitType
              controlRef={profileControl}
              onStateChange={(p) => setProfileReady(isProfileStepComplete(p))}
              onLoadState={setProfileLoadFailed}
            />
          </div>
        </>
      ) : (
        // #872 (D172 S2) — the one field the drawing asks for, and nothing
        // else. `nw-kit-input` is the same placeholder-colour class
        // `PlayerProfile.jsx`'s kitType Club/Team fields use (index.css).
        <input
          ref={shortGoalRef}
          data-testid="pi-short-goal"
          className="nw-kit-input"
          value={shortGoal}
          onChange={(e) => {
            visionDirtyRef.current = true;
            setShortGoal(e.target.value);
          }}
          placeholder="Type it here — one sentence is plenty."
          style={{
            minHeight: "var(--nw-size-control)",
            padding: "0 12px",
            background: "var(--nw-surface)",
            border: "1px solid var(--nw-line)",
            borderRadius: "var(--nw-radius-control)",
            fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
            fontSize: 15,
            color: "var(--nw-ink)",
          }}
        />
      )}

      {/* #872 — the echo card, live on every keystroke, rendered only once
          the trimmed sentence is non-empty (Plan.dc.html's `set-up-step-2`
          state; PlanScreen.jsx's own #763 card follows the identical rule).
          `gap={6}`, not the kit's default 8 — the drawn state's own card
          uses a tighter gap than PlanScreen's (see QuoteEchoCard.jsx's own
          comment). Helper text stops at "Your sentence, back before
          anything else happens." — D172 S5: the drawn line continues "It
          starts your plan.", which is false today (PlanScreen.jsx never
          reads `short_term_goal`, audit-verify.md G9.5), so the false
          clause is dropped rather than shipped; the wording itself goes to
          the next design round (the D97 route, brief-g59.md's own note). */}
      {!isProfileStep && trimmedShortGoal && (
        <QuoteEchoCard quote={trimmedShortGoal} gap={6} helper="Your sentence, back before anything else happens." />
      )}

      {/* #868 (D172 S6) — SetUp.dc.html's own `save-failed` state, verbatim
          copy, D55's ink-on-`--nw-line-faint`-with-alert-rule treatment
          (the same shape `PlanScreen.jsx`'s `goal-save-error` and
          `LogMatchScorecardStep.jsx`'s `ErrorBanner` already use) — never a
          toast on this leg (D57). `PlayerProfile.jsx`'s own `save()`
          suppresses its toast for `kitType`; this is what replaces it. */}
      {isProfileStep && profileSaveFailed && (
        <ErrorBanner testId="onboarding-save-error">{STEP_SAVE_FAILED}</ErrorBanner>
      )}

      {/* #872 (PR #937 review, F2) — step 2's own two failure modes, same
          D57/D55 treatment: never a toast on this leg. "load" is the
          identity GET that never resolved (saveVisionStep's own refusal —
          saving on top of it would overwrite the other seven vision
          answers with nothing); "save" is the PUT itself failing. Two
          testids, not one, because the two are genuinely different
          failures a reviewer or a journey needs to tell apart, and #937's
          own review forces each independently (identity GET 500 vs PUT
          500). */}
      {!isProfileStep && visionSaveError === "load" && (
        <ErrorBanner testId="onboarding-vision-load-error">
          Couldn't load your saved answers — reload the page before saving.
        </ErrorBanner>
      )}
      {!isProfileStep && visionSaveError === "save" && (
        <ErrorBanner testId="onboarding-vision-save-error">{STEP_SAVE_FAILED}</ErrorBanner>
      )}

      {/* #872 (PR #937 review, F2) — `stopAsking`'s own failure
          (`/onboarding/complete`), shared by Skip (either step) and
          Finish (step 2, after its own save already succeeded). One
          banner, rendered regardless of step, because it is the one
          call both actions route through. #938 nit 2, D182 (Andreas,
          2026-09-30): the old "We couldn't save that just now" was wrong
          when Finish had already saved the sentence and only finishing
          set-up failed, and on Skip, where nothing was being saved
          either way — one string for both paths, since D182 ruled the
          same words for Finish and Skip. */}
      {completeFailed && (
        <ErrorBanner testId="onboarding-complete-error">
          We couldn't finish setting up just now — please try again.
        </ErrorBanner>
      )}

      {/* #872 (G9.2) — Plan.dc.html's `set-up-step-2` state draws no hint
          line at all (only step-label/title/lede, the field, the echo,
          then the footer row) — this stays step-1-only. */}
      {isProfileStep && !profileLoadFailed && (
        // #1077 — hidden while the load-failure block shows: its "choose a
        // playing role…" would describe a form that is not on screen.
        // #868 (G5.5) — SetUp.dc.html:81 draws this hint at 13px, one
        // above `.nw-help`'s own 12px (index.css) — a fixed inline
        // override rather than a fourth type-ramp class, since `.nw-help`
        // is shared by many undrawn-at-13px callers (PlanScreen.jsx among
        // them) that must not move.
        <p id="onboarding-hint-text" className="nw-help" data-testid="onboarding-hint" style={{ fontSize: 13 }}>
          {canContinue
            ? "That's everything we need for now."
            : "Choose a playing role, and a batting or bowling style, to continue."}
        </p>
      )}

      {/* PR #826 review, footer fold-in — `gap-2.5` (Tailwind, `rem`)
          rendered this row's gap at 10.6px against SetUp.dc.html:117's
          drawn `gap: 10px`. `var(--nw-space-inline)` (index.css, new) is
          fixed px — a flex-row gap, not a vertical stack, so this stays an
          inline style rather than one of the `.nw-stack-*` margin
          utilities. */}
      <div className="flex items-center" style={{ gap: "var(--nw-space-inline)" }}>
        {/* Skip is real and permanent as far as the router is concerned —
            see Onboarding.jsx's note on why there is no "unsaved work"
            warning. */}
        <SecondaryAction
          variant="boxed"
          onClick={skip}
          disabled={busy}
          data-testid="onboarding-skip"
        >
          Skip for now
        </SecondaryAction>
        {/* Issue #793: `ActionButton` spans its row by default now (kit's
            own `display:flex; width:100%`), but THIS site keeps its own
            `flexGrow: 1` — measured, not assumed, and the measurement
            said no: dropping it and relying on the kit's `width:100%`
            alone changed the geometry (`onboarding-continue` grew from
            200.4px to 232.5px, and — the actual defect — SecondaryAction
            "Skip for now" has no `white-space: nowrap`, so the flex-shrink
            algorithm that then kicks in to fit both items shrinks Skip's
            TEXT, not just its box). `width: "auto"` cancels the kit's own
            width so the flex-basis stays content-driven, exactly as it
            was before this issue: Skip is still the one item genuinely
            pinned by its content, so `flexGrow: 1` gives this button
            precisely what's left beside it — the same pixel width as
            today, per
            `frontend/e2e/journeys/30-action-button-width.journey.spec.js`. */}
        {isProfileStep ? (
          <ActionButton
            onClick={handleContinueTap}
            disabled={busy}
            aria-disabled={!canContinue}
            // PR #937 review, F4 — the hint right above (`onboarding-hint`)
            // is the one thing on screen that says WHY Continue is
            // `aria-disabled` ("Choose a playing role, and a batting or
            // bowling style, to continue."); wiring it up as this
            // button's description is what lets a screen reader announce
            // that reason, rather than just "dimmed" — sorry, "disabled" —
            // with no explanation. Present whether or not it's currently
            // disabled — the same hint also confirms completion once it
            // is enabled, so there's no state where pointing to it is
            // wrong.
            aria-describedby={profileLoadFailed ? undefined : "onboarding-hint-text"}
            data-testid="onboarding-continue"
            style={{ flexGrow: 1, width: "auto" }}
          >
            {busy ? "Saving…" : "Continue"}
          </ActionButton>
        ) : (
          <ActionButton
            onClick={finish}
            disabled={busy || !canContinue}
            data-testid="onboarding-finish"
            style={{ flexGrow: 1, width: "auto" }}
          >
            {busy ? "Saving…" : "Finish"}
          </ActionButton>
        )}
      </div>
    </div>
  );
}

/**
 * PR #937 review, F2 — the one on-page failure shape D57/D55 asks for,
 * shared by all four banners above (step 1's save, step 2's load-refusal,
 * step 2's save, and the shared `/onboarding/complete` failure) rather
 * than four copies of the same style object. Same properties `PlanScreen.
 * jsx`'s `goal-save-error` and `LogMatchScorecardStep.jsx`'s own
 * `ErrorBanner` already use — ink text, `--nw-line-faint` fill, a 3px
 * `--nw-alert` left rule.
 */
function ErrorBanner({ testId, children }) {
  return (
    <div
      data-testid={testId}
      style={{
        fontSize: 13,
        lineHeight: 1.5,
        color: "var(--nw-ink)",
        padding: "10px 12px",
        background: "var(--nw-line-faint)",
        borderLeft: "3px solid var(--nw-alert)",
        borderRadius: "var(--nw-radius-control)",
      }}
    >
      {children}
    </div>
  );
}
